import { useQuery } from "@tanstack/react-query";
import {
  FileText,
  Users,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent, StatusBadge, Loading } from "@/components/ui";
import { budgetsApi, clientsApi } from "@/services/api";

function StatCard({
  icon: Icon,
  label,
  value,
  trend,
  color = "accent-primary",
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  trend?: string;
  color?: string;
}) {
  return (
    <Card>
      <CardContent className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs sm:text-sm text-text-muted mb-1 truncate">
            {label}
          </p>
          <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-text-primary truncate">
            {value}
          </p>
          {trend && (
            <p className="text-xs sm:text-sm text-green-400 mt-2 flex items-center gap-1">
              <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
              <span className="truncate">{trend}</span>
            </p>
          )}
        </div>
        <div className={`p-2 sm:p-3 rounded-xl bg-${color}/10 flex-shrink-0`}>
          <Icon className={`w-5 h-5 sm:w-6 sm:h-6 text-${color}`} />
        </div>
      </CardContent>
    </Card>
  );
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

export default function DashboardPage() {
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["budget-stats"],
    queryFn: budgetsApi.stats,
  });

  const { data: recentBudgets, isLoading: budgetsLoading } = useQuery({
    queryKey: ["recent-budgets"],
    queryFn: () =>
      budgetsApi.list({
        per_page: 5,
        sort_by: "created_at",
        sort_order: "desc",
      }),
  });

  const { data: clientsData } = useQuery({
    queryKey: ["clients-count"],
    queryFn: () => clientsApi.list({ per_page: 1 }),
  });

  const budgetStats = stats?.data || {};
  const totalClients = clientsData?.data?.total || 0;

  if (statsLoading) {
    return <Loading message="Carregando dashboard..." />;
  }

  return (
    <div className="space-y-4 sm:space-y-6 lg:space-y-8 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">
          Dashboard
        </h1>
        <p className="text-text-secondary mt-1 text-sm sm:text-base">
          Visão geral do sistema de orçamentos
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        <StatCard
          icon={FileText}
          label="Total de Orçamentos"
          value={budgetStats.total || 0}
        />
        <StatCard
          icon={Users}
          label="Clientes Cadastrados"
          value={totalClients}
          color="accent-secondary"
        />
        <StatCard
          icon={DollarSign}
          label="Valor Aprovado"
          value={formatCurrency(budgetStats.approved_value || 0)}
          color="green-400"
        />
        <StatCard
          icon={Clock}
          label="Aguardando Resposta"
          value={formatCurrency(budgetStats.pending_value || 0)}
          color="yellow-400"
        />
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Budget Status */}
        <Card>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-text-primary">
              Status dos Orçamentos
            </h2>
          </div>
          <CardContent>
            <div className="space-y-4">
              {[
                {
                  key: "draft",
                  label: "Rascunho",
                  icon: FileText,
                  color: "text-text-muted",
                },
                {
                  key: "sent",
                  label: "Enviados",
                  icon: Clock,
                  color: "text-blue-400",
                },
                {
                  key: "viewed",
                  label: "Visualizados",
                  icon: Eye,
                  color: "text-yellow-400",
                },
                {
                  key: "approved",
                  label: "Aprovados",
                  icon: CheckCircle,
                  color: "text-green-400",
                },
                {
                  key: "rejected",
                  label: "Rejeitados",
                  icon: XCircle,
                  color: "text-red-400",
                },
              ].map(({ key, label, icon: Icon, color }) => (
                <div key={key} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Icon className={`w-5 h-5 ${color}`} />
                    <span className="text-text-secondary">{label}</span>
                  </div>
                  <span className="text-text-primary font-semibold">
                    {budgetStats[key] || 0}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Budgets */}
        <Card>
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-semibold text-text-primary">
              Orçamentos Recentes
            </h2>
            <Link
              to="/admin/budgets"
              className="text-xs sm:text-sm text-accent-primary hover:underline flex items-center gap-1"
            >
              Ver todos <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
            </Link>
          </div>
          <CardContent>
            {budgetsLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="h-14 sm:h-16 bg-white/5 rounded-lg animate-pulse"
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-2 sm:space-y-3">
                {recentBudgets?.data?.items?.slice(0, 5).map((budget: any) => (
                  <Link
                    key={budget.id}
                    to={`/admin/budgets/${budget.id}`}
                    className="flex items-center justify-between p-2 sm:p-3 rounded-xl hover:bg-white/5 active:bg-white/10 transition-colors gap-2"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-text-primary font-medium text-sm sm:text-base truncate">
                        {budget.number}
                      </p>
                      <p className="text-xs sm:text-sm text-text-muted truncate">
                        {budget.client_name}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-text-primary font-medium text-sm sm:text-base">
                        {formatCurrency(budget.total)}
                      </p>
                      <StatusBadge status={budget.status} />
                    </div>
                  </Link>
                ))}

                {(!recentBudgets?.data?.items ||
                  recentBudgets.data.items.length === 0) && (
                  <p className="text-center text-text-muted py-8">
                    Nenhum orçamento criado ainda
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
        <Link to="/admin/budgets/new">
          <Card className="text-center py-6 sm:py-8 hover:border-accent-primary/50 active:bg-white/5">
            <FileText className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-accent-primary mb-3 sm:mb-4" />
            <h3 className="text-base sm:text-lg font-semibold text-text-primary">
              Novo Orçamento
            </h3>
            <p className="text-xs sm:text-sm text-text-muted mt-1">
              Criar um novo orçamento
            </p>
          </Card>
        </Link>

        <Link to="/admin/clients/new">
          <Card className="text-center py-6 sm:py-8 hover:border-accent-primary/50 active:bg-white/5">
            <Users className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-accent-secondary mb-3 sm:mb-4" />
            <h3 className="text-base sm:text-lg font-semibold text-text-primary">
              Novo Cliente
            </h3>
            <p className="text-xs sm:text-sm text-text-muted mt-1">
              Cadastrar um cliente
            </p>
          </Card>
        </Link>

        <Link to="/admin/products/new">
          <Card className="text-center py-6 sm:py-8 hover:border-accent-primary/50 active:bg-white/5">
            <DollarSign className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-accent-tertiary mb-3 sm:mb-4" />
            <h3 className="text-base sm:text-lg font-semibold text-text-primary">
              Novo Produto
            </h3>
            <p className="text-xs sm:text-sm text-text-muted mt-1">
              Adicionar produto/serviço
            </p>
          </Card>
        </Link>
      </div>
    </div>
  );
}
